package fr.frux

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.ContentTransform
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.NavEntryDecorator
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import coil3.ImageLoader
import coil3.compose.setSingletonImageLoaderFactory
import coil3.network.ktor3.KtorNetworkFetcherFactory
import coil3.request.crossfade
import fr.frux.components.InstallBanner
import fr.frux.components.rememberInstallPrompt
import fr.frux.core.ads.AdHold
import fr.frux.core.ads.AdsConsentIntroHost
import fr.frux.core.ads.HoldAds
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.persistentStorageAvailable
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.components.LocalNavigationBackInterceptors
import fr.frux.core.designsystem.components.NavigationBackInterceptors
import fr.frux.core.designsystem.layout.FruxLayoutDefaults
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.layout.LocalRootChromeInsets
import fr.frux.core.designsystem.layout.fruxLayoutMode
import fr.frux.core.designsystem.theme.FruxTheme
import fr.frux.di.FruxCompositionRoot
import fr.frux.feature.map.MapHostState
import fr.frux.feature.map.MapSurface
import fr.frux.feature.map.rememberMapHostState
import fr.frux.feature.support.SupportPromptHost
import fr.frux.navigation.AddTreeOrigin
import fr.frux.navigation.AppLink
import fr.frux.navigation.AppLinkEffect
import fr.frux.navigation.BindPlatformNavigation
import fr.frux.navigation.BottomNavigation
import fr.frux.navigation.InterstitialNavigationEffects
import fr.frux.navigation.LayoutZone
import fr.frux.navigation.MapPanel
import fr.frux.navigation.NavigationState
import fr.frux.navigation.RailNavigation
import fr.frux.navigation.Route
import fr.frux.navigation.WIDE_METADATA_KEY
import fr.frux.navigation.ZONE_METADATA_KEY
import fr.frux.navigation.addTreeRouteFrom
import fr.frux.navigation.consumeInitialDeepLink
import fr.frux.navigation.fruxEntryProvider
import fr.frux.navigation.handleNavBack
import fr.frux.navigation.homeTab
import fr.frux.navigation.isMapInteractive
import fr.frux.navigation.keepsAim
import fr.frux.navigation.layoutZone
import fr.frux.navigation.railSelection
import fr.frux.navigation.rememberNavigationState
import fr.frux.navigation.resolveStartRoute
import fr.frux.navigation.shouldShowBottomNav
import fr.frux.shared.di.AppComponent
import fr.frux.shared.domain.session.SessionState
import fr.frux.splash.SplashOverlay
import kotlinx.coroutines.launch

/**
 * Main App composable for Frux.
 *
 * Startup is driven by the observable [fr.frux.shared.domain.session.SessionStore]:
 * the session is restored first (splash while [SessionState.Unknown]), then the
 * navigation stack is created on the right screen — the app never reads token
 * presence directly.
 *
 * Navigation uses Navigation 3 UI (NavDisplay/NavBackStack API):
 * - Developer-owned back stack (full control)
 * - Type-safe navigation, automatic state persistence
 * - Works on Android, iOS, Desktop, Web
 *
 * @param component Graphe Metro, fourni par le point d'entrée de la plateforme. Il n'est plus lu
 *   depuis un singleton global : `App` est le composition root, et n'expose aux écrans que des
 *   fabriques typées (cf. [FruxCompositionRoot], #43).
 * @param notificationTapId Tap sur une notification système (#252 P2) : une valeur non nulle pousse
 *   [Route.Notifications] sur la pile. Chaque tap porte une valeur distincte (l'hôte Android en
 *   dérive une de `System.nanoTime()`) pour que deux taps successifs, y compris pendant que l'app
 *   est déjà au premier plan, retriggent la navigation. Cf. `MainActivity`.
 * @param appLink App Link reçu à chaud (#704) : l'hôte Android en pose un nouveau, d'`id` distinct,
 *   à chaque réception. Un lien reçu à froid passe par le registre de démarrage, pas par ici.
 * @param onSplashFinished Appelé une fois, quand l'overlay de splash disparaît. Sert à l'hôte
 *   Android à restaurer le style de barre système par défaut (le splash force des icônes lisibles
 *   sur son fond crème le temps qu'il est affiché, cf. `MainActivity`). No-op sur les autres cibles.
 */
@Composable
fun App(
    component: AppComponent,
    notificationTapId: Long? = null,
    appLink: AppLink? = null,
    onSplashFinished: () -> Unit = {},
) {
    // Singleton unique pour toute l'app : le crossfade se règle ici, pas requête par requête
    // (FruxRemoteImage, core-designsystem).
    //
    // KtorNetworkFetcherFactory() sans argument ouvre son propre HttpClient() par défaut — un
    // second client Ktor/OkHttp, isolé du client applicatif (FruxHttpClient, core-network), avec
    // son propre pool de connexions et ses propres délais. Sur l'émulateur Android, ce client
    // jetable, non réchauffé, entrait en concurrence avec les requêtes MapLibre pour ouvrir de
    // nouvelles connexions vers l'alias NAT 10.0.2.2 — la poignée de main TCP expirait
    // silencieusement (`ConnectTimeoutException`, ~10 s, jamais logué : `FruxRemoteImage` ne trace
    // pas `AsyncImagePainter.State.Error`), et la photo restait bloquée sur son repli. Le web ne
    // reproduisait pas le bug : le moteur `js` passe par le `fetch` du navigateur, mutualisé avec
    // le reste de la page, sans ce second pool de sockets.
    //
    // Fix : réutiliser le client unique du graphe Metro (`component.getHttpClient()`,
    // `NetworkModule`), déjà configuré (timeouts, retry sur GET) et déjà chaud — plus de second
    // client à faire vivre.
    setSingletonImageLoaderFactory { context ->
        ImageLoader
            .Builder(context)
            .components { add(KtorNetworkFetcherFactory(httpClient = { component.getHttpClient() })) }
            .crossfade(true)
            .build()
    }

    FruxCompositionRoot(component) {
        FruxTheme {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = MaterialTheme.colorScheme.background,
            ) {
                Box(Modifier.fillMaxSize()) {
                    var splashVisible by rememberSaveable { mutableStateOf(true) }
                    // Colonne toujours présente : la bannière d'installation (web Android) prend sa
                    // propre hauteur au-dessus du contenu sans changer la position de composition
                    // d'AppContent, dont la pile de navigation ne doit pas être réinitialisée.
                    val installPrompt = rememberInstallPrompt()
                    Column(Modifier.fillMaxSize()) {
                        if (!splashVisible && installPrompt != null) InstallBanner(installPrompt)
                        Box(
                            // Tant que le splash couvre l'écran, l'app dessous ne doit plus être
                            // atteignable par TalkBack/VoiceOver (sinon la carte masquée reste focusable).
                            modifier =
                                Modifier
                                    .weight(1f)
                                    .fillMaxWidth()
                                    .then(if (splashVisible) Modifier.clearAndSetSemantics {} else Modifier),
                        ) {
                            AppContent(component, notificationTapId, appLink)
                        }
                    }
                    // Avant splash et écran publicitaire : ils passent au-dessus du bandeau (#610).
                    var storageNoticeVisible by remember { mutableStateOf(!persistentStorageAvailable) }
                    if (!splashVisible && storageNoticeVisible) {
                        StorageUnavailableNotice(
                            onDismiss = { storageNoticeVisible = false },
                            modifier = Modifier.align(Alignment.TopCenter),
                        )
                    }
                    if (splashVisible) {
                        SplashOverlay(
                            onFinished = {
                                splashVisible = false
                                onSplashFinished()
                            },
                        )
                    } else {
                        // Écran d'information publicitaire (#527), jamais sous le splash : le
                        // contrôleur garde l'état IntroPending tant qu'il n'est pas affiché.
                        AdsConsentIntroHost()
                    }
                }
            }
        }
    }
}

/**
 * Démarrage et navigation. Sépare du root le seul étage qui lit le graphe directement : session et
 * onboarding, dont `App` est le propriétaire légitime (personne d'autre ne les observe).
 */
@Composable
private fun AppContent(
    component: AppComponent,
    notificationTapId: Long?,
    appLink: AppLink?,
) {
    val sessionStore = remember { component.getSessionStore() }
    val onboardingStore = remember { component.getOnboardingStore() }
    val sessionState by sessionStore.state.collectAsState()
    var onboardingCompleted by remember { mutableStateOf<Boolean?>(null) }

    // Ni publicité ni appel à UMP ou à la régie tant que l'onboarding n'est pas fini (ADR 0030
    // point 5, #527). Posé dès la première composition, état encore inconnu compris : le hold est
    // appliqué avant que le contrôleur, construit par le composition root, ne lise quoi que ce soit.
    HoldAds(AdHold.ONBOARDING, active = onboardingCompleted != true)

    LaunchedEffect(sessionStore) {
        onboardingCompleted = onboardingStore.isCompleted()
        sessionStore.restore()
    }

    // Le store n'a pas de scope propre : c'est App qui héberge la collecte des
    // expirations signalées par la couche réseau, pour toute la vie de l'application (#48).
    LaunchedEffect(sessionStore) {
        sessionStore.observeSessionExpiry()
    }

    // Reaching an authenticated session (login, register or restore)
    // implies onboarding is done for good.
    LaunchedEffect(sessionState) {
        if (sessionState is SessionState.Authenticated && onboardingCompleted != true) {
            onboardingStore.markCompleted()
            onboardingCompleted = true
        }
    }

    SessionBoundStores(component, sessionState)

    PushPermissionReconciliation(component, sessionState)

    val scope = rememberCoroutineScope()

    // Resolved once when the session restore completes, then frozen:
    // the NavBackStack owns navigation from that point on and must not
    // see its initial route change mid-session.
    //
    // Le lien profond d'ouverture (web, #299) est consommé au même instant : il pèse sur la base
    // de la pile pour un visiteur sans session (#385), il doit donc être connu avant qu'elle
    // existe. Consommé une seule fois par processus, il ne rejoue jamais sur une pile reconstruite.
    var initialRoute by remember { mutableStateOf<Route?>(null) }
    var initialDeepLink by remember { mutableStateOf<Route?>(null) }
    val onboardingResolved = onboardingCompleted
    if (initialRoute == null && onboardingResolved != null && sessionState !is SessionState.Unknown) {
        val deepLink = consumeInitialDeepLink()
        initialDeepLink = deepLink
        initialRoute = resolveStartRoute(sessionState, onboardingResolved, deepLink)
    }

    // Stable lambdas: NavigationState is remembered against them and
    // must survive recompositions (it holds the pending route).
    val isAuthenticated =
        remember(sessionStore) {
            { sessionStore.state.value is SessionState.Authenticated }
        }
    val onOnboardingComplete =
        remember(onboardingStore) {
            {
                onboardingCompleted = true
                scope.launch { onboardingStore.markCompleted() }
                Unit
            }
        }

    when (val startRoute = initialRoute) {
        null -> {
            SplashScreen()
        }

        else -> {
            AuthenticatedOrAnonymousApp(
                component = component,
                startRoute = startRoute,
                initialDeepLink = initialDeepLink,
                sessionState = sessionState,
                isAuthenticated = isAuthenticated,
                onOnboardingComplete = onOnboardingComplete,
                notificationTapId = notificationTapId,
                appLink = appLink,
            )
        }
    }
}

/**
 * Les stores qui suivent la session : chargés à son ouverture, vidés à sa fermeture. Extrait
 * d'[AppContent] pour rester sous les seuils de detekt, sans changer de comportement.
 */
@Composable
private fun SessionBoundStores(
    component: AppComponent,
    sessionState: SessionState,
) {
    // Le store des favoris suit la session : chargé dès qu'elle s'ouvre, vidé quand elle se
    // ferme — les écrans ne déclenchent jamais ce cycle eux-mêmes (source unique, spec
    // 2026-08-12-map-favorites-store).
    val favoritesStore = remember(component) { component.getFavoritesStore() }
    LaunchedEffect(sessionState) {
        when (sessionState) {
            is SessionState.Authenticated -> {
                favoritesStore.refresh().onFailure {
                    Logger.d(TAG, "⚠️ Chargement des favoris au login échoué : ${it.message}")
                }
            }

            is SessionState.Anonymous, is SessionState.Expired -> {
                favoritesStore.clear()
            }

            else -> {
                Unit
            } // Unknown : la restauration est en cours, ne rien décider.
        }
    }

    // Même cycle que favoritesStore (#225 P0) : le badge de la barre du bas ne se rafraîchit pas
    // lui-même, il vit dans un VM permanent (NotificationBadgeStore) suivi ici.
    val notificationBadgeStore = remember(component) { component.getNotificationBadgeStore() }
    LaunchedEffect(sessionState) {
        when (sessionState) {
            is SessionState.Authenticated -> {
                notificationBadgeStore.refresh().onFailure {
                    Logger.d(TAG, "⚠️ Chargement du compteur de notifications au login échoué : ${it.message}")
                }
            }

            is SessionState.Anonymous, is SessionState.Expired -> {
                notificationBadgeStore.clear()
            }

            else -> {
                Unit
            } // Unknown : la restauration est en cours, ne rien décider.
        }
    }
}

/**
 * Un retrait de POST_NOTIFICATIONS dans les réglages système après activation du push laisse le
 * serveur router vers un canal mort (#285) : `pushEnabled` et un jeton actif survivent, le système
 * supprime silencieusement les bannières et l'email ne prend jamais le relais. À l'ouverture de
 * session, si la permission n'est plus là, la préférence serveur repasse à false — silencieux, ce
 * n'est pas un geste de l'utilisateur ; réactiver le push redevient un opt-in explicite depuis
 * l'écran de préférences.
 */
@Composable
private fun PushPermissionReconciliation(
    component: AppComponent,
    sessionState: SessionState,
) {
    val pushRegistrar = remember(component) { component.getPushRegistrar() }
    LaunchedEffect(sessionState) {
        if (sessionState !is SessionState.Authenticated) return@LaunchedEffect
        if (!pushRegistrar.supported || pushRegistrar.permissionGranted()) return@LaunchedEffect
        val notificationRepository = component.getNotificationRepository()
        notificationRepository
            .getPreferences()
            .mapCatching { prefs ->
                if (prefs.pushEnabled) {
                    notificationRepository
                        .updatePreferences(
                            emailEnabled = prefs.emailEnabled,
                            pushEnabled = false,
                            harvestEvents = prefs.harvestEvents,
                            treeEvents = prefs.treeEvents,
                            maturityAlerts = prefs.maturityAlerts,
                        ).getOrThrow()
                    Logger.i(TAG, "Permission de notification révoquée : préférence push repassée à false")
                }
            }.onFailure {
                Logger.d(TAG, "⚠️ Réconciliation push au login échouée : ${it.message}")
            }
    }
}

@Composable
private fun AuthenticatedOrAnonymousApp(
    component: AppComponent,
    startRoute: Route,
    initialDeepLink: Route?,
    sessionState: SessionState,
    isAuthenticated: () -> Boolean,
    onOnboardingComplete: () -> Unit,
    notificationTapId: Long?,
    appLink: AppLink?,
) {
    val navigationState =
        rememberNavigationState(
            startRoute = startRoute,
            isAuthenticated = isAuthenticated,
        )

    // Position déclinée dans l'accueil (#502) : mémoire de session seulement, jamais persistée.
    // La carte n'existe pas encore à ce moment (mapHost null sous Welcome), d'où ce relais.
    val locationSettledByOnboarding = rememberSaveable { mutableStateOf(false) }

    // Web : le back du navigateur pope la pile au lieu de quitter le site — après avoir laissé
    // les surcouches (bottom sheets) le consommer, et en ramenant les onglets vers la carte.
    // Ailleurs, NavDisplay consulte les mêmes intercepteurs via handleNavBack (Android : fenêtres
    // de dialogue M3 pour les feuilles, handler dédié pour l'assistant d'ajout d'arbre).
    val backInterceptors = remember { NavigationBackInterceptors() }
    BindPlatformNavigation(navigationState, backInterceptors)

    // Deep link web (#299) : l'URL d'ouverture cible un écran, empilé par-dessus la base une fois
    // la session résolue. `navigate()` applique le mur d'auth existant (route murée → Auth +
    // reprise post-OTP) ; URL inconnue = null, comportement d'ouverture inchangé. Le drapeau est
    // sauvegardé avec la pile : une pile restaurée après mort de processus contient déjà l'écran.
    var deepLinkHandled by rememberSaveable { mutableStateOf(false) }
    LaunchedEffect(navigationState) {
        if (!deepLinkHandled) {
            deepLinkHandled = true
            initialDeepLink?.let { navigationState.navigate(it) }
        }
    }

    // Tap sur une notification système (#252 P2) : ouvre l'écran Activité — `navigationState`
    // gère déjà le report vers Auth si la session n'est pas encore restaurée authentifiée. Keyé
    // aussi sur `notificationTapId` (pas seulement `navigationState`) : un second tap alors que
    // l'app est déjà au premier plan doit retrigger la navigation sans reconstruire l'écran.
    LaunchedEffect(navigationState, notificationTapId) {
        if (notificationTapId != null) navigationState.navigate(Route.Notifications)
    }

    val currentRouteForMap = navigationState.backStack.lastOrNull()

    // Accueil revisité (déconnexion d'un compte jamais allé au bout) : même règle que l'onboarding.
    HoldAds(AdHold.ONBOARDING, active = currentRouteForMap is Route.Welcome)
    InterstitialNavigationEffects(navigationState.backStack)

    // La carte ne démarre qu'à la première visite d'un écran qui en a besoin — pas pendant
    // Welcome/Auth, où charger des arbres serait du travail invisible. Une fois vraie, la surface
    // ne quitte plus jamais la composition : c'est tout l'objet de sa persistance (#124).
    var mapEverNeeded by rememberSaveable { mutableStateOf(false) }
    // Récoltes en fait partie (#534) : l'onglet liste la zone de la carte, il lui faut sa caméra.
    // Le déplacement aussi : une fiche ouverte par lien direct sur téléphone n'a jamais montré la
    // carte, et le déplacement se fait dessus.
    if (currentRouteForMap is Route.Map || currentRouteForMap?.keepsAim() == true ||
        currentRouteForMap is Route.Harvests
    ) {
        mapEverNeeded = true
    }
    val mapHost = if (mapEverNeeded) rememberMapHostState() else null
    AppLinkEffect(navigationState, appLink, mapHost)

    // Garde anti-fuite de la visée : quelle que soit la sortie de l'assistant (retour, connexion
    // expirée, deep link), la carte ne reste jamais en visée hors de Route.AddTree et Route.MoveTree.
    LaunchedEffect(currentRouteForMap) {
        // Le démarrage différé des adaptateurs vérifie la route lui-même : rien à annuler ici.
        if (currentRouteForMap?.keepsAim() != true) mapHost?.aim?.stop()
    }

    val treePhotoUploads = remember(component) { component.getTreePhotoUploads() }

    // Perdre sa session, par expiration ou par déconnexion volontaire, doit ramener l'app à un
    // état cohérent : pile vidée, écran de connexion, et surface carte purgée — elle survit à la
    // navigation (ADR 0009) donc personne d'autre ne la nettoie (#48).
    //
    // On réagit à la transition *sortante* d'une session authentifiée, jamais à l'état courant :
    // au démarrage, Unknown → Anonymous ne doit rien déclencher.
    var wasAuthenticated by remember { mutableStateOf(sessionState is SessionState.Authenticated) }
    LaunchedEffect(sessionState) {
        val isAuthenticatedNow = sessionState is SessionState.Authenticated
        if (wasAuthenticated && !isAuthenticatedNow) {
            mapHost?.reset()
            // Les envois d'un compte ne doivent pas continuer sous un autre.
            treePhotoUploads.clear()
            navigationState.resetTo(Route.Auth)
        }
        wasAuthenticated = isAuthenticatedNow
    }

    CompositionLocalProvider(LocalNavigationBackInterceptors provides backInterceptors) {
        AppSkeleton(
            navigationState = navigationState,
            sessionState = sessionState,
            onOnboardingComplete = onOnboardingComplete,
            locationSettledByOnboarding = locationSettledByOnboarding,
            mapHost = mapHost,
        )
    }
}

/**
 * Squelette racine, en dessous de la mesure de largeur : extrait d'[AuthenticatedOrAnonymousApp]
 * pour rester sous les seuils de complexité de detekt (#69), sans changer de comportement.
 */
@Composable
private fun AppSkeleton(
    navigationState: NavigationState,
    sessionState: SessionState,
    onOnboardingComplete: () -> Unit,
    locationSettledByOnboarding: MutableState<Boolean>,
    mapHost: MapHostState?,
) {
    // Mode d'affichage (spec 2026-09-10, §2) : mesuré ici, au-dessus de tout ce qui porte de
    // l'état (pile, ViewModels, MapHostState). Un redimensionnement ne change que le squelette.
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val layoutMode = fruxLayoutMode(maxWidth.value.toInt())
        CompositionLocalProvider(LocalFruxLayoutMode provides layoutMode) {
            val currentRoute = navigationState.backStack.lastOrNull()
            val showBottomNav = currentRoute?.shouldShowBottomNav() == true
            // Pendant l'animation de sortie, currentRoute n'est déjà plus un onglet : on garde
            // le dernier onglet vu pour que la barre ne change pas de sélection en disparaissant.
            var lastTabRoute by remember { mutableStateOf<Route?>(null) }
            if (showBottomNav) lastTabRoute = currentRoute

            // Carte vivante à côté d'une colonne (spec §5) : en Expanded, sur toute route de
            // MapZone, jamais sur Route.Map elle-même (qui est MapChromeZone, l'écran carte y
            // est composé et gère déjà ses propres taps de marqueur).
            val besidePanel = layoutMode == FruxLayoutMode.Expanded && currentRoute?.layoutZone() == LayoutZone.MapZone

            val mapSurface: @Composable () -> Unit = {
                // Surface carte persistante, SOUS le NavDisplay : les écrans opaques la masquent
                // sans la détruire (ADR 0009). Interactive sur Explorer, et en grand écran à côté
                // d'une colonne (lot 2, #376).
                if (mapHost != null) {
                    MapSurface(
                        host = mapHost,
                        interactive =
                            currentRoute?.isMapInteractive(layoutMode, aimActive = mapHost.aim.isActive) == true,
                        onMarkerOpen =
                            if (besidePanel) {
                                { id -> navigationState.openTreeInPanel(id) }
                            } else {
                                null
                            },
                        onPlaqueOpen =
                            if (besidePanel) {
                                { id -> navigationState.openSiteInPanel(id) }
                            } else {
                                null
                            },
                    )
                }
            }
            val navContent: @Composable () -> Unit = {
                MainNavDisplay(
                    navigationState = navigationState,
                    onOnboardingComplete = onOnboardingComplete,
                    locationSettledByOnboarding = locationSettledByOnboarding,
                    mapHost = mapHost,
                    mode = layoutMode,
                )
            }

            // Un seul squelette pour les deux modes, jamais un `when` entre deux composables :
            // le mode et la zone ne pilotent que des modificateurs et la présence du rail ou de
            // la barre. Deux squelettes sœurs donneraient deux sites de composition à `mapSurface`
            // et à `content`, donc une carte MapLibre détruite et reconstruite à chaque
            // franchissement du seuil (contraire à l'ADR 0009) et des ViewModels de NavEntry
            // purgés par le décorateur de pile à chaque bascule (spec §2, aucun état perdu).
            RootChrome(
                mode = layoutMode,
                zone = currentRoute?.layoutZone() ?: LayoutZone.EntryZone,
                rail = {
                    RailNavigation(
                        // Le rail ne disparaît jamais : il éclaire l'onglet de ce que l'on voit,
                        // pas le dernier onglet vu (« Ajouter » depuis Profil s'ouvre sur la carte).
                        currentRoute = railSelection(navigationState.backStack),
                        onNavigate = { route -> navigationState.replaceWith(route) },
                        onAddTreeClick = {
                            navigationState.navigate(addTreeRouteFrom(mapHost?.camera?.position, AddTreeOrigin.MAP_FAB))
                        },
                    )
                },
                bottomBar = {
                    AnimatedVisibility(
                        visible = showBottomNav,
                        enter = fadeIn() + slideInVertically(initialOffsetY = { it }),
                        exit = fadeOut() + slideOutVertically(targetOffsetY = { it }),
                    ) {
                        lastTabRoute?.let { tabRoute ->
                            BottomNavigation(
                                currentRoute = tabRoute,
                                onNavigate = { route -> navigationState.replaceWith(route) },
                                // Doit fonctionner depuis n'importe quel onglet : AddTree est
                                // autonome (sa propre carte, sa propre géoloc), donc un simple
                                // `navigate` suffit.
                                onAddTreeClick = {
                                    navigationState.navigate(
                                        addTreeRouteFrom(mapHost?.camera?.position, AddTreeOrigin.MAP_FAB),
                                    )
                                },
                            )
                        }
                    }
                },
                mapSurface = mapSurface,
                content = navContent,
            )

            // Au-dessus de tout : la feuille de soutien n'appartient à aucun écran (spec 2026-08-13).
            // Rien pour une session anonyme : la sollicitation et son ViewModel exigent un compte
            // (endpoints authentifiés, cf. SupportRoutes.kt).
            SupportPromptHost(
                isMapVisible = currentRoute is Route.Map,
                isAuthenticated = sessionState is SessionState.Authenticated,
            )
        }
    }
}

/**
 * Squelette racine unique de l'application, téléphone et grand écran (spec 2026-09-10 webApp
 * grand écran, §4) : bottom bar dans le slot d'un Scaffold transparent (#85), rail à gauche en
 * grand écran, carte persistante et pile de navigation empilées dans le contenu, pleine hauteur.
 *
 * Unique, et pas deux squelettes sœurs choisis par un `when` : [mapSurface] et [content] n'ont
 * qu'un seul site de composition, quels que soient [mode] et [zone]. Les groupes de composition
 * sont identifiés par leur position dans la source, donc un `when` entre deux squelettes détruit
 * le groupe d'une branche et en crée un autre. Ce qui partirait avec lui : la carte MapLibre, que
 * l'ADR 0009 veut jamais démontée, et tous les ViewModel de NavEntry, purgés par
 * `rememberViewModelStoreNavEntryDecorator` dès que son parent quitte la composition. Ici [mode]
 * et [zone] ne pilotent que des modificateurs, la présence du rail et celle de la barre du bas :
 * franchir 840 dp ou passer de la carte au profil ne remesure que le squelette.
 *
 * Le padding du Scaffold n'est volontairement PAS appliqué au contenu : la carte et le chrome de
 * l'écran carte (MapScreen) doivent rester pleine hauteur sous edge-to-edge. Seules les valeurs de
 * dégagement sont propagées, via [LocalRootChromeInsets] (quatre côtés, lu par la carte) et
 * [LocalBottomNavPadding] (bas seul, lu par les onglets), à charge pour chaque écran de les
 * appliquer où il en a besoin. Le Scaffold ignore aussi ses insets système (contentWindowInsets) :
 * chaque écran gère déjà les siens. En grand écran rien ne recouvre le bas, le dégagement vaut
 * zéro.
 *
 * @param mapSurface carte persistante (ADR 0009), composée SOUS [content].
 * @param content pile de navigation, reçoit [LocalBottomNavPadding].
 */
@Composable
internal fun RootChrome(
    mode: FruxLayoutMode,
    zone: LayoutZone,
    rail: @Composable () -> Unit,
    bottomBar: @Composable () -> Unit,
    mapSurface: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    val expanded = mode == FruxLayoutMode.Expanded
    val fullZone = expanded && zone == LayoutZone.FullZone
    // Le passage d'une zone à l'autre se fond par la couleur de fond de l'hôte, jamais par un
    // Crossfade du contenu : un Crossfade compose chaque valeur cible sous sa propre clé, donc
    // deux piles de navigation vivantes et deux jeux de ViewModel le temps de l'animation.
    val zoneBackground by animateColorAsState(
        targetValue = if (fullZone) MaterialTheme.colorScheme.background else Color.Transparent,
        label = "zoneBackground",
    )
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        containerColor = Color.Transparent,
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = { if (!expanded) bottomBar() },
    ) { bottomBarPadding ->
        Row(modifier = Modifier.fillMaxSize()) {
            if (expanded && zone != LayoutZone.EntryZone) rail()
            // La carte vit DANS le contenu du Scaffold, pas en sœur dessous : la Surface Material 3
            // qui porte ce contenu absorbe les événements pointeur (`pointerInput(Unit) {}`), même
            // transparente, et une carte composée sous le Scaffold ne recevait plus aucun geste
            // (régression #85, cf. RootChromeTest). Le contenu étant mesuré pleine taille, la carte
            // n'est jamais rognée par la bottom bar ni par son animation.
            // clipToBounds : la surcouche de marqueurs (core-maps) dessine des marqueurs partiellement
            // hors de la carte, qui débordaient sur le rail à gauche en grand écran.
            Box(modifier = Modifier.weight(1f).fillMaxHeight().clipToBounds()) {
                val bottomInset = if (expanded) 0.dp else bottomBarPadding.calculateBottomPadding()
                val rootInsets =
                    PaddingValues(
                        start = if (expanded && zone == LayoutZone.MapZone) FruxLayoutDefaults.MapPanelWidth else 0.dp,
                        bottom = bottomInset,
                    )
                // Fourni à la carte ET à la pile : la carte persistante, toujours vivante, en
                // déduit le décalage de son attribution quand une colonne est ouverte, alors que
                // l'écran carte n'est plus composé (spec §5, tâche 4 du lot 2).
                CompositionLocalProvider(
                    LocalRootChromeInsets provides rootInsets,
                    LocalBottomNavPadding provides bottomInset,
                ) {
                    mapSurface()
                    Box(
                        modifier = Modifier.fillMaxSize().background(zoneBackground),
                        contentAlignment = if (fullZone) Alignment.TopCenter else Alignment.TopStart,
                    ) {
                        Box(modifier = zoneHostModifier(mode, zone)) { content() }
                    }
                }
            }
        }
    }
}

/**
 * Habillage de l'hôte de contenu selon le mode et la zone (spec §4). Fonction pure de modificateurs,
 * volontairement hors composition : changer de modificateur remesure le nœud, changer de composable
 * le détruirait. La colonne de `MapZone` n'est pas ici : c'est l'écran empilé qui la porte
 * ([fr.frux.navigation.MapPanel]), pour que l'hôte garde la même largeur d'un écran à l'autre.
 */
private fun zoneHostModifier(
    mode: FruxLayoutMode,
    zone: LayoutZone,
): Modifier =
    if (mode == FruxLayoutMode.Compact) {
        Modifier.fillMaxSize()
    } else {
        when (zone) {
            // Explorer nu comme fiche en colonne : la pile couvre toute la zone à droite du rail,
            // sans pointerInput, pour que les gestes hors colonne atteignent la carte dessous.
            LayoutZone.MapChromeZone, LayoutZone.MapZone -> {
                Modifier.fillMaxSize()
            }

            // Hôte sur toute la zone, la même largeur pour tous ses écrans : chacun pose lui-même
            // sa colonne (cf. MapPanel, #443), centrée pour les écrans étroits, contre le rail pour
            // le Profil. Une largeur d'hôte variant avec l'écran ferait sauter l'écran sortant.
            LayoutZone.FullZone -> {
                Modifier.fillMaxSize()
            }

            LayoutZone.EntryZone -> {
                Modifier.fillMaxSize()
            }
        }
    }

@Composable
private fun MainNavDisplay(
    navigationState: NavigationState,
    onOnboardingComplete: () -> Unit,
    locationSettledByOnboarding: MutableState<Boolean>,
    mapHost: MapHostState?,
    mode: FruxLayoutMode,
) {
    val transitions = remember(mode) { ZoneTransitions(mode) }
    val backInterceptors = LocalNavigationBackInterceptors.current
    transitions.onTopRoute(navigationState.backStack.lastOrNull())
    // La colonne grand écran est portée par chaque entrée de `MapZone` (cf. MapPanel), la zone
    // voyageant dans les métadonnées de l'entrée (mapPanelMetadata) : la clé de contenu Nav3
    // n'est pas la route, et l'écran qui sort après un pop n'est déjà plus dans la pile.
    val panelDecorator =
        remember {
            NavEntryDecorator<Route>({}) { entry ->
                MapPanel(
                    zone = entry.metadata[ZONE_METADATA_KEY] as? LayoutZone,
                    wide = entry.metadata[WIDE_METADATA_KEY] == true,
                ) { entry.Content() }
            }
        }
    NavDisplay(
        backStack = navigationState.backStack,
        onBack = { handleNavBack(backInterceptors, navigationState) },
        transitionSpec = { transitions.push() },
        popTransitionSpec = { transitions.pop() },
        predictivePopTransitionSpec = {
            slideInHorizontally(initialOffsetX = { -it / 3 }) togetherWith
                slideOutHorizontally(targetOffsetX = { it })
        },
        entryDecorators =
            listOf(
                // Save/restore screen state across process death (Android)
                rememberSaveableStateHolderNavEntryDecorator(),
                // ViewModels scoped to NavEntry and cleared when entry is removed
                rememberViewModelStoreNavEntryDecorator(),
                panelDecorator,
            ),
        entryProvider =
            fruxEntryProvider(
                navigationState = navigationState,
                onOnboardingComplete = onOnboardingComplete,
                onLocationDeclinedInOnboarding = { locationSettledByOnboarding.value = true },
                locationSettledByOnboarding = locationSettledByOnboarding.value,
                mapHost = mapHost,
            ),
    )
}

/**
 * Transitions du NavDisplay selon les zones grand écran (spec §4). En compact : glissement
 * horizontal classique. En grand écran, un changement de zone (Profil en pleine zone vers
 * « Ajouter » en colonne sur la carte) est déjà un fondu du conteneur de zone, y ajouter le
 * glissement ferait rétrécir l'écran sortant dans la colonne pendant qu'il glisse. Et la colonne
 * se pose sur la carte depuis la gauche, sous le rail, et y repart : glisser depuis la droite la
 * faisait surgir du milieu de la carte.
 *
 * Les zones viennent de la pile, pas des scènes du NavDisplay : la clé de contenu d'une entrée
 * Nav3 est `defaultContentKey(route)`, une chaîne, pas la route. [onTopRoute] est appelée à
 * chaque composition avec le sommet de pile et garde la zone du sommet précédent, celle que
 * l'écran sortant occupait.
 */
private class ZoneTransitions(
    private val mode: FruxLayoutMode,
) {
    private val expanded get() = mode == FruxLayoutMode.Expanded
    private var currentRoute: Route? = null
    private var currentZone: LayoutZone? = null
    private var previousZone: LayoutZone? = null

    fun onTopRoute(route: Route?) {
        if (route == currentRoute) return
        previousZone = currentZone
        currentRoute = route
        currentZone = route?.layoutZone()
    }

    private fun crossesZone(): Boolean = expanded && previousZone?.homeTab() != currentZone?.homeTab()

    private fun panelOpens(): Boolean =
        expanded && previousZone == LayoutZone.MapChromeZone && currentZone == LayoutZone.MapZone

    private fun panelCloses(): Boolean =
        expanded && previousZone == LayoutZone.MapZone && currentZone == LayoutZone.MapChromeZone

    // Colonne déjà ouverte : son contenu change sur place, sans animation. Un fondu entre deux
    // colonnes opaques laissait la carte transparaître à mi-course, ce qui se lisait comme une
    // colonne qui bouge. Seules la première ouverture et la dernière fermeture s'animent.
    private fun withinPanel(): Boolean =
        expanded && previousZone == LayoutZone.MapZone && currentZone == LayoutZone.MapZone

    // Accueil et Connexion portent le même visuel de marque à gauche (SplitEntryLayout) : un
    // glissement faisait croiser deux logos identiques (#414). L'écran du dessus apparaît ou
    // disparaît en fondu au-dessus de l'autre, resté opaque, et le logo ne bouge pas d'un pixel.
    // Nav3 place l'entrée poussée au-dessus et l'entrée dépilée au-dessus de celle qu'elle révèle.
    private fun withinEntry(): Boolean =
        expanded && previousZone == LayoutZone.EntryZone && currentZone == LayoutZone.EntryZone

    // Slide horizontal plutôt que le fondu par défaut : le fondu superpose deux écrans complets
    // pendant la transition, ce qui se lit mal quand les deux portent une carte. L'écran
    // sous-jacent glisse au tiers (parallaxe) pour marquer la profondeur de pile.
    fun push(): ContentTransform =
        when {
            crossesZone() -> {
                fadeIn() togetherWith fadeOut()
            }

            panelOpens() -> {
                slideInHorizontally(initialOffsetX = { -it }) togetherWith fadeOut()
            }

            withinPanel() -> {
                EnterTransition.None togetherWith ExitTransition.None
            }

            withinEntry() -> {
                fadeIn(tween(ENTRY_FADE_MILLIS)) togetherWith fadeOut(snap(delayMillis = ENTRY_FADE_MILLIS))
            }

            else -> {
                slideInHorizontally(initialOffsetX = { it }) togetherWith
                    slideOutHorizontally(targetOffsetX = { -it / 3 })
            }
        }

    fun pop(): ContentTransform =
        when {
            crossesZone() -> {
                fadeIn() togetherWith fadeOut()
            }

            panelCloses() -> {
                fadeIn() togetherWith slideOutHorizontally(targetOffsetX = { -it })
            }

            withinPanel() -> {
                EnterTransition.None togetherWith ExitTransition.None
            }

            withinEntry() -> {
                EnterTransition.None togetherWith fadeOut(tween(ENTRY_FADE_MILLIS))
            }

            else -> {
                slideInHorizontally(initialOffsetX = { -it / 3 }) togetherWith
                    slideOutHorizontally(targetOffsetX = { it })
            }
        }
}

/** Shown while the session is being restored — avoids flashing the wrong screen. */
@Composable
private fun SplashScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator()
    }
}

private const val TAG = "App"

private const val ENTRY_FADE_MILLIS = 250
